import React, { useRef, useState } from "react";
import { Box, IconButton, Stack, TextField, Typography, useTheme } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { useIntl } from "react-intl";
import { getAssetHierarchyPalette, getIconButtonSx, getSelectSx } from "../assetHierarchyStyles";
import cmmsMessages from "../cmmsMessages";

const rowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "130px 1fr" },
  alignItems: "center",
  gap: 1,
  py: 1,
};

const valueText = (value) => (value === null || value === undefined ? "" : String(value));

/*
 * A label and its value, and when `canEdit`, a value you change where it stands.
 *
 * Click the value (or tab to it and press Enter) and it becomes a field with its text selected,
 * so typing replaces it. Enter or clicking away saves, Escape puts it back; the ✓ and ✕ do the
 * same for anyone who would rather click. That is the whole interaction, and it is the one a
 * spreadsheet cell has, which is the point: nothing to learn.
 *
 * The pencil is always there, not only on hover, because a tablet has no hover and a value that
 * gives no sign it can be changed will not be.
 *
 * `parse` turns the typed text into the value to save and `validate` refuses one with a message,
 * which stays under the field; the field does not close on a value it cannot save. An unchanged
 * value closes without a request. `onSave` resolves to whether it saved, and a failed save also
 * leaves the field open with what was typed.
 *
 * `disabled` holds off a save while the node is still being saved or re-read. Every save sends
 * the node's other fields as last read, so a second save started before the first one's re-read
 * would put the first one's field back.
 */
export default function EditableDetailLine({
  label,
  value,
  emptyText,
  canEdit = false,
  disabled = false,
  inputProps,
  parse = (text) => text,
  validate = () => null,
  onSave,
}) {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  /*
   * Refs, not state, for the two things blur has to know. Enter saves and then disables the field,
   * Escape closes it, and either can be followed by a blur from the same field in the same tick,
   * before a state update has rendered. Read from state, that blur would save a second time, or
   * save what Escape just threw away.
   */
  const isOpenRef = useRef(false);
  const isSavingRef = useRef(false);

  const isEditing = draft !== null;
  const current = valueText(value);
  const isEmpty = current.trim() === "";

  const startEditing = () => {
    isOpenRef.current = true;
    setError(null);
    setDraft(current);
  };

  const cancelEditing = () => {
    isOpenRef.current = false;
    setError(null);
    setDraft(null);
  };

  const save = async () => {
    if (!isOpenRef.current || isSavingRef.current || disabled) return;

    if (draft.trim() === current.trim()) {
      cancelEditing();
      return;
    }

    const parsed = parse(draft);
    const validationError = validate(parsed);
    if (validationError) {
      setError(validationError);
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      const saved = await onSave(parsed);
      if (saved) cancelEditing();
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      save();
    } else if (event.key === "Escape") {
      event.preventDefault();
      cancelEditing();
    }
  };

  // Keeps the field focused when ✓ or ✕ is pressed. Without it the field blurs first, and the blur
  // has already saved by the time ✕ is clicked.
  const keepFocus = (event) => event.preventDefault();

  const labelCell = (
    <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontWeight: 700 }}>
      {label}
    </Typography>
  );

  const valueTypography = (
    <Typography
      variant="body2"
      sx={{ color: isEmpty ? cmmsColors.mutedText : cmmsColors.text, fontWeight: isEmpty ? 400 : 700, wordBreak: "break-word" }}
    >
      {isEmpty ? emptyText : current}
    </Typography>
  );

  if (!canEdit) {
    return (
      <Box sx={rowSx}>
        {labelCell}
        {valueTypography}
      </Box>
    );
  }

  if (isEditing) {
    return (
      <Box sx={rowSx}>
        {labelCell}
        <Stack direction="row" alignItems="flex-start" spacing={0.5}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            value={draft}
            disabled={isSaving}
            error={Boolean(error)}
            helperText={error}
            onChange={(event) => {
              setDraft(event.target.value);
              setError(null);
            }}
            onKeyDown={handleKeyDown}
            onBlur={save}
            onFocus={(event) => event.target.select()}
            inputProps={{ ...inputProps, "aria-label": label }}
            InputProps={{
              sx: {
                ...getSelectSx(cmmsColors),
                // getSelectSx paints a focused field green, and the field is always focused while
                // it shows an error; the extra class lets the error's red win.
                "&.MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" },
              },
            }}
            FormHelperTextProps={{ sx: { mx: 0 } }}
          />
          <IconButton
            size="small"
            aria-label={intl.formatMessage(cmmsMessages.save)}
            onMouseDown={keepFocus}
            onClick={save}
            disabled={isSaving}
            sx={{ ...getIconButtonSx(cmmsColors), color: cmmsColors.accent, mt: 0.25 }}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={intl.formatMessage(cmmsMessages.cancel)}
            onMouseDown={keepFocus}
            onClick={cancelEditing}
            disabled={isSaving}
            sx={{ ...getIconButtonSx(cmmsColors), mt: 0.25 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={rowSx}>
      {labelCell}
      <Box
        role="button"
        tabIndex={0}
        aria-label={`${intl.formatMessage(cmmsMessages.edit)}: ${label}`}
        onClick={startEditing}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            startEditing();
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          // Bled out by its own padding so the text still lines up with the read-only rows.
          mx: -1,
          px: 1,
          py: 0.5,
          my: -0.5,
          borderRadius: 0.5,
          cursor: "pointer",
          "& .editable-detail-pencil": { opacity: 0.45 },
          "&:hover, &:focus-visible": { backgroundColor: cmmsColors.buttonHover, outline: "none" },
          "&:hover .editable-detail-pencil, &:focus-visible .editable-detail-pencil": { opacity: 1 },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>{valueTypography}</Box>
        <EditIcon className="editable-detail-pencil" sx={{ fontSize: 16, color: cmmsColors.mutedText }} />
      </Box>
    </Box>
  );
}
